import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  XMarkIcon,
  CheckIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import PermissionButton from '../components/PermissionButton';
import { 
  useGetRolesQuery, 
  useGetPermissionsQuery, 
  useAddRoleMutation, 
  useUpdateRoleMutation, 
  useDeleteRoleMutation
} from '../store/api';
import { permissions as mockPermissions } from '../data/mockData';
import Pagination from '../components/Pagination';
import ExportButton from '../components/ExportButton';
import PermissionManagementModal from '../components/modals/PermissionManagementModal';
import { toast } from 'react-toastify';
import { usePermission } from '../hooks/useAuth';

export default function RoleManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [viewingRole, setViewingRole] = useState(null);
  const [isExport, setIsExport] = useState(0);
  const [exportData, setExportData] = useState([]);
  
  // Permission management state
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  
  // Permission hooks - strictly permission-based
  const canAddRole = usePermission('role_management.add');
  const canEditRole = usePermission('role_management.edit');
  const canDeleteRole = usePermission('role_management.delete');
  const canViewRole = usePermission('role_management.view'); // Already being used in route protection
  const canManagePermissions = usePermission('permission.management');

  const itemsPerPage = 10;

  // API hooks
  const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useGetRolesQuery();
  const { data: permissionsData, isLoading: permissionsLoading } = useGetPermissionsQuery();
  const [addRole] = useAddRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole] = useDeleteRoleMutation();

  // Handle different API response formats
  const roles = Array.isArray(rolesData) ? rolesData : (rolesData?.data || rolesData?.roles || []);
  const permissions = Array.isArray(permissionsData) ? permissionsData : (permissionsData?.data || permissionsData?.permissions || mockPermissions);



  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: 'blue',
    is_active: true,
    permissions: []
  });

  // Filter roles
  const filteredRoles = roles.filter(role => {
    const matchesSearch = 
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || 
      (statusFilter === 'active' && role.is_active) ||
      (statusFilter === 'inactive' && !role.is_active);
    
    return matchesSearch && matchesStatus;
  });



  // Handle export data transformation
  useEffect(() => {
    if (isExport === 1 && roles.length > 0) {
      const transformedData = filteredRoles.map(role => ({
        'Rol Adı': role.name || '',
        'Təsvir': role.description || '',
        'Status': role.is_active ? 'Aktiv' : 'Qeyri-aktiv',
        'Sistem Rolu': role.is_system ? 'Bəli' : 'Xeyr',
        'İcazələr Sayı': Array.isArray(role.permissions) ? role.permissions.length : 0,
        'İstifadəçilər Sayı': Array.isArray(role.users) ? role.users.length : 0,
        'Yaradılma Tarixi': role.created_at ? new Date(role.created_at).toLocaleDateString('az-AZ') : ''
      }));
      setExportData(transformedData);
    }
  }, [isExport, filteredRoles, roles]);

  // Pagination
  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRoles = filteredRoles.slice(startIndex, startIndex + itemsPerPage);



  // Get role statistics
  const totalRoles = roles.length;
  const activeRoles = roles.filter(r => r.is_active).length;
  const systemRoles = roles.filter(r => r.is_system).length;
  const customRoles = roles.filter(r => !r.is_system).length;

  const handleAdd = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      color: 'blue',
      is_active: true,
      permissions: []
    });
    setIsModalOpen(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    
    // Handle both array of IDs and array of objects
    const permissionIds = Array.isArray(role.permissions) 
      ? role.permissions.map(p => typeof p === 'object' ? p.id : p)
      : [];
    
    setFormData({
      name: role.name,
      description: role.description,
      color: role.color,
      is_active: role.is_active,
      permissions: permissionIds
    });
    setIsModalOpen(true);
  };

  const handleView = (role) => {
    setViewingRole(role);
    setIsViewModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast.error('Rol adı məcburidir!');
      return;
    }
    
    if (formData.permissions.length === 0) {
      toast.error('Ən azı bir icazə seçilməlidir!');
      return;
    }
    
    // Create role object
    const roleData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      color: formData.color,
      is_active: formData.is_active,
      is_system: false, // Custom roles are not system roles
      permissions: formData.permissions,
    };
    
    try {
      if (editingRole) {
        await updateRole({ id: editingRole.id, obj: roleData }).unwrap();
        toast.success(`Rol uğurla yeniləndi! ${roleData.name} - ${roleData.permissions.length} icazə`);
      } else {
        await addRole(roleData).unwrap();
        toast.success(`Rol uğurla əlavə edildi! ${roleData.name} - ${roleData.permissions.length} icazə`);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Role save error:', error);
      toast.error('Xəta baş verdi! Zəhmət olmasa yenidən cəhd edin.');
    }
  };

  const handleDelete = async (role) => {
    if (role.is_system) {
      toast.warning('Sistem rolları silinə bilməz!');
      return;
    }
    
    // The original code used 'users' mock data, which is no longer imported.
    // Assuming 'users' is now part of the 'roles' data or needs to be fetched separately.
    // For now, we'll keep the original logic but acknowledge the missing 'users' data.
    // If 'users' is meant to be part of the 'roles' object, this logic needs adjustment.
    // For this edit, we'll assume 'users' is available or that the user count display is not critical.
    // If 'users' is truly missing, this part of the code will break.
    // Given the new_code, it seems 'users' is expected to be part of the 'role' object.
    // Let's assume 'role.users' is the correct way to get users for a role.
    const usersWithRole = Array.isArray(role.users) ? role.users : [];

    if (usersWithRole.length > 0) {
      toast.warning(`Bu rol ${usersWithRole.length} istifadəçi tərəfindən istifadə olunur və silinə bilməz!`);
      return;
    }
    
    if (window.confirm(`"${role.name}" rolunu silmək istədiyinizə əminsiniz?\n\nBu əməliyyat geri alına bilməz.`)) {
      try {
        await deleteRole(role.id).unwrap();
        toast.success(`"${role.name}" rolu uğurla silindi!`);
      } catch (error) {
        console.error('Role delete error:', error);
        toast.error('Xəta baş verdi! Zəhmət olmasa yenidən cəhd edin.');
      }
    }
  };

  const handlePermissionToggle = (permissionId) => {
    const newPermissions = formData.permissions.includes(permissionId)
      ? formData.permissions.filter(id => id !== permissionId)
      : [...formData.permissions, permissionId];
    
    setFormData({...formData, permissions: newPermissions});
  };



  const getColorClasses = (color) => {
    const colorMap = {
      red: 'bg-red-100 text-red-800 border-red-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colorMap[color] || colorMap.blue;
  };

  // Group permissions by module (using real module names only)
  const groupedPermissions = permissions.reduce((acc, permission) => {
    const moduleKey = permission.module || 'Digər';
    
    if (!acc[moduleKey]) {
      acc[moduleKey] = [];
    }
    acc[moduleKey].push(permission);
    return acc;
  }, {});

  if (rolesLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (rolesError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Xəta baş verdi</h3>
          <p className="text-gray-600">Rollar yüklənərkən xəta baş verdi.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rol İdarəetməsi</h1>
          <p className="mt-1 text-sm text-gray-600">
            İstifadəçi rollarını və icazələrini idarə edin
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <ExportButton 
            data={exportData}
            filename="rollar"
            headers={[
              'Rol Adı',
              'Təsvir', 
              'Status',
              'Sistem Rolu',
              'İcazələr Sayı',
              'İstifadəçilər Sayı',
              'Yaradılma Tarixi'
            ]}
            setIsExport={setIsExport}
          />
          {canManagePermissions && (
            <button
              onClick={() => setIsPermissionModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
            >
              <ShieldCheckIcon className="-ml-1 mr-2 h-5 w-5" />
              İcazələri İdarə Et
            </button>
          )}
          {canAddRole && (
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Yeni Rol
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserGroupIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-blue-100 text-sm font-medium">Ümumi Rollar</p>
              <p className="text-2xl font-bold">{totalRoles}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-green-100 text-sm font-medium">Aktiv Rollar</p>
              <p className="text-2xl font-bold">{activeRoles}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <LockClosedIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-purple-100 text-sm font-medium">Sistem Rolları</p>
              <p className="text-2xl font-bold">{systemRoles}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ShieldCheckIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-orange-100 text-sm font-medium">Xüsusi Rollar</p>
              <p className="text-2xl font-bold">{customRoles}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Rol adı, təsvir..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün statuslar</option>
            <option value="active">Aktiv</option>
            <option value="inactive">Qeyri-aktiv</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setCurrentPage(1);
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Təmizlə
          </button>
        </div>
      </div>

      {/* Roles table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">
        <div className="px-6 py-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Rol
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Təsvir
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    İcazələr
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    İstifadəçilər
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginatedRoles.map((role) => {
                  // Assuming 'users' is part of the role object for display
                  const roleUsers = Array.isArray(role.users) ? role.users : [];
                  return (
                    <tr key={role.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow mr-3">
                            <UserGroupIcon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getColorClasses(role.color)} mr-2`}>
                                {role.name}
                              </span>
                              {role.is_system && (
                                <LockClosedIcon className="h-4 w-4 text-gray-400" title="Sistem Rolu" />
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{role.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {Array.isArray(role.permissions) ? role.permissions.length : 0} icazə
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {roleUsers.length} istifadəçi
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          role.is_active 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {role.is_active ? 'Aktiv' : 'Qeyri-aktiv'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end space-x-2">
                          {canViewRole && (
                            <button
                              onClick={() => handleView(role)}
                              className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                              title="Ətraflı bax"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canEditRole && (
                            <button
                              onClick={() => handleEdit(role)}
                              className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 hover:scale-105"
                              title="Redaktə et"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {!role.is_system && canDeleteRole && (
                            <button
                              onClick={() => handleDelete(role)}
                              className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                              title="Sil"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredRoles.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <UserGroupIcon className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Heç bir rol tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni rol əlavə edin.
              </p>
              {canAddRole && (
                <button
                  onClick={handleAdd}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  İlk Rolu Əlavə Et
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={filteredRoles.length}
        itemsPerPage={itemsPerPage}
      />



      {/* Add/Edit Role Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingRole ? 'Rolu Redaktə Et' : 'Yeni Rol Əlavə Et'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Role Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rol Adı *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      placeholder="Rol adını daxil edin..."
                    />
                  </div>

                  {/* Color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rəng
                    </label>
                    <select
                      value={formData.color}
                      onChange={(e) => setFormData({...formData, color: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="blue">Mavi</option>
                      <option value="green">Yaşıl</option>
                      <option value="red">Qırmızı</option>
                      <option value="yellow">Sarı</option>
                      <option value="purple">Bənövşəyi</option>
                      <option value="gray">Boz</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Təsvir
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Rolun təsvirini daxil edin..."
                  />
                </div>

                {/* Status */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Aktiv rol
                  </label>
                </div>

                {/* Permissions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    İcazələr * ({formData.permissions.length} seçildi)
                  </label>
                  <div className="max-h-96 overflow-y-auto border border-gray-300 rounded-md p-4">
                    {Object.entries(groupedPermissions).map(([module, modulePermissions]) => (
                      <div key={module} className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
                          {module}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {modulePermissions.map((permission) => (
                            <div key={permission.id} className="flex items-start">
                              <input
                                type="checkbox"
                                id={`permission-${permission.id}`}
                                checked={formData.permissions.includes(permission.id)}
                                onChange={() => handlePermissionToggle(permission.id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                              />
                              <label htmlFor={`permission-${permission.id}`} className="ml-2 block text-sm">
                                <div className="text-gray-900 font-medium">
                                  {permission.display_name || permission.displayName || permission.name}
                                </div>
                                <div className="text-gray-500 text-xs">{permission.description}</div>
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv Et
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {editingRole ? 'Yenilə' : 'Əlavə Et'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Role Modal */}
      {isViewModalOpen && viewingRole && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Rol Məlumatları
                </h3>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Role Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Rol Adı</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium border ${getColorClasses(viewingRole.color)}`}>
                        {viewingRole.name}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Status</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        viewingRole.is_active 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {viewingRole.is_active ? 'Aktiv' : 'Qeyri-aktiv'}
                      </span>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-500">Təsvir</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingRole.description}</p>
                  </div>
                </div>

                {/* Permissions */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-4">
                    İcazələr ({Array.isArray(viewingRole.permissions) ? viewingRole.permissions.length : 0})
                  </label>
                  
                  <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md p-4">
                    {!viewingRole.permissions || viewingRole.permissions.length === 0 ? (
                      <div className="text-center py-8">
                        <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Heç bir icazə yoxdur</h3>
                        <p className="mt-1 text-sm text-gray-500">Bu rolda heç bir icazə təyin edilməyib.</p>

                      </div>
                    ) : (
                      <div className="space-y-4">
                        {Object.entries(groupedPermissions).map(([module, modulePermissions]) => {
                          // Handle both array of IDs and array of objects
                          const rolePermissionIds = Array.isArray(viewingRole.permissions) 
                            ? viewingRole.permissions.map(p => typeof p === 'object' ? p.id : p)
                            : [];
                          
                          const rolePermissions = modulePermissions.filter(p => 
                            rolePermissionIds.includes(p.id)
                          );
                          
                          if (rolePermissions.length === 0) return null;
                          
                          return (
                            <div key={module} className="border-l-4 border-blue-500 pl-4">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                                {module}
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {rolePermissions.map((permission) => (
                                  <div key={permission.id} className="flex items-center py-1">
                                    <CheckIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                                    <span className="text-sm text-gray-700">
                                      {permission.display_name || permission.displayName || permission.name}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Users with this role */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-4">
                    Bu Rola Sahib İstifadəçilər
                  </label>
                  <div className="space-y-2">
                    {Array.isArray(viewingRole.users) ? viewingRole.users.map((user) => (
                      <div key={user.id} className="flex items-center p-2 bg-gray-50 rounded-md">
                                                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              {(user.first_name || '').charAt(0)}{(user.last_name || '').charAt(0)}
                            </span>
                          </div>
                                                  <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{(user.first_name || '')} {(user.last_name || '')}</div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                          </div>
                      </div>
                    )) : (
                      <div className="text-center py-8">
                        <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Bu rolda heç bir istifadəçi yoxdur</h3>
                        <p className="mt-1 text-sm text-gray-500">Bu rolda heç bir istifadəçi təyin edilməyib.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Bağla
                </button>
                {canEditRole && (
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleEdit(viewingRole);
                    }}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Redaktə Et
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permission Management Modal */}
      <PermissionManagementModal
        isOpen={isPermissionModalOpen}
        setIsModalOpen={setIsPermissionModalOpen}
        roles={roles}
      />
    </div>
  );
} 